<template>
  <div>
    <browser-warning/>
    <trial-subscription-warning/>
    <no-subscription-warning/>

    <div class="navigation-top align-center">
      <div class="d-flex justify-space-between pa-0" style="height: 48px; min-width: 100%">
        <div class="d-flex mr-2" style="height: 48px; line-height: 48px;">
          <img
            style="width: 140px"
            :class="['nav-logo', {'cursor-pointer': subscriptionAccessType !== 'NO_ACCESS'}]"
            src="/continuum-design-logo.svg"
            alt="Continuum Design"
            @click="subscriptionAccessType !== 'NO_ACCESS' ? $router.push('/dashboard/').catch(() => {}): null"
          />
          <org-switcher v-if="subscriptionAccessType !== 'NO_ACCESS'"/>
          <navigation-links v-if="subscriptionAccessType !== 'NO_ACCESS'"/>
          <workflow-selector v-if="$route.params.id && subscriptionAccessType !== 'NO_ACCESS'" ref="workflowSelector"/>
        </div>

        <slot>
        <!-- slot for injecting additional buttons -->
        </slot>

        <div class="d-flex flex-nowrap ml-8">
          <v-menu
            location="bottom center"
            :offset="[8, 0]"
            v-if="subscriptionAccessType === 'FULL'"
          >
            <template #activator="{ props }">
              <v-btn
                v-bind="props"
                variant="text"
                class="mr-4"
                icon="mds:add_circle"
              />
            </template>

            <v-list density="compact">
              <settings-project-dialog :setProject="true"
                                       v-if="selectedPlan && currentOrgMemberships.length < selectedPlan.maxProjectsCount">
                <template #activator="{ props }">
                  <v-list-item v-bind="props" prepend-icon="mds:grid-view">
                    <v-list-item-title>New project</v-list-item-title>
                  </v-list-item>
                </template>
              </settings-project-dialog>

              <workflow-dialog
                v-if="isNewWorkflowEnabled()"
                :setWorkflow="true"
                :memberships="filteredProjectMemberships"
              >
                <template #activator="{ props }">
                  <v-list-item v-bind="props" prepend-icon="mds:account-tree">
                    <v-list-item-title>New workflow</v-list-item-title>
                  </v-list-item>
                </template>
              </workflow-dialog>
            </v-list>
          </v-menu>

          <help-dialog-workflow v-if="isWorkflowPage"/>
          <help-dialog-project v-if="isProjectPage"/>
          <help-dialog-start v-if="isStartPage"/>

          <v-btn
            class="mr-4"
            variant="text"
            :icon="themeIcon"
            @click="toggleTheme"
          />

          <org-settings-button v-if="subscriptionAccessType !== 'NO_ACCESS'"/>

          <user-profile-header/>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import UserProfileHeader from '@/components/UserProfileHeader'
import OrgSettingsButton from './OrgSettingsButton.vue'
import OrgSwitcher from './OrgSwitcher.vue'
import NavigationLinks from './NavigationLinks.vue'
import WorkflowSelector from './WorkflowSelector.vue'
import WorkflowDialog from './WorkflowDialog.vue'
import SettingsProjectDialog from './SettingsProjectDialog.vue'
import HelpDialogWorkflow from './HelpDialogWorkflow'
import HelpDialogProject from './HelpDialogProject'
import HelpDialogStart from './HelpDialogStart'
import { mapState, mapGetters } from 'vuex'
import { computed } from 'vue'
import { ENTERPRISE_PLAN, getPlanByPriceId } from '@/utils/payments'
import BrowserWarning from '@/components/BrowserWarning.vue'
import NoSubscriptionWarning from '@/components/NoSubscriptionWarning.vue'
import TrialSubscriptionWarning from '@/components/TrialSubscriptionWarning.vue'
import useAppTheme from '@/composables/useAppTheme'

export default {
  setup () {
    const { themePreference, toggleTheme } = useAppTheme()
    const themeIcon = computed(() => {
      return themePreference.value === 'dark' ? 'mds:light_mode' : 'mds:dark_mode'
    })
    return { themePreference, toggleTheme, themeIcon }
  },
  components: {
    TrialSubscriptionWarning,
    NoSubscriptionWarning,
    BrowserWarning,
    OrgSettingsButton,
    OrgSwitcher,
    NavigationLinks,
    WorkflowSelector,
    UserProfileHeader,
    WorkflowDialog,
    SettingsProjectDialog,
    HelpDialogWorkflow,
    HelpDialogProject,
    HelpDialogStart
  },
  computed: {
    ...mapState({
      activeSubscription: state => state.payments.activeSubscription,
      subscriptionAccessType: state => state.payments.subscriptionAccessType,
      isEnterpriseUser: state => state.payments.isEnterpriseUser
    }),
    ...mapGetters({
      currentOrgId: 'currentOrg/currentOrgId',
      currentOrgMemberships: 'orgProjects/currentOrgMemberships'
    }),
    // Projects come from the org-native orgProjects store (listOrgProjects for the
    // current org), NOT the legacy projectMemberships list (null for org users).
    hasProjects () {
      return this.currentOrgMemberships.length > 0
    },
    filteredProjectMemberships () {
      return this.currentOrgMemberships.filter(pm => pm.project)
    },
    isWorkflowPage () {
      return !!this.$route.params.id
    },
    isProjectPage () {
      return (!!this.$route.params.projectId) && (!this.$route.params.id)
    },
    isStartPage () {
      return (!this.$route.params.projectId) && (!this.$route.params.id)
    },
    selectedPlan: function () {
      if (this.activeSubscription) {
        if (this.isEnterpriseUser) {
          return ENTERPRISE_PLAN
        }
        return getPlanByPriceId(this.activeSubscription?.subscriptionDetails?.price?.id)
      }
      return null
    }
  },
  watch: {
    currentOrgId: {
      handler (orgId) {
        if (orgId) {
          this.$store.dispatch('orgProjects/loadOrgProjects', { orgId })
        }
      },
      immediate: true
    }
  },
  methods: {
    isNewWorkflowEnabled () {
      return this.hasProjects && !this.$store.state.workflow.isViewOnlyEnabled
    }
  }
}
</script>

<style scoped>
.navigation-top {
  margin: 14px -16px;
}
</style>
